//
// Copyright (c) 2011-2020 by Curalate, Inc.
//
import _ from 'underscore';

import 'app/components/content/content-library-labels-modal/LabelsModal';

import * as gatewayMediaApi from '@apis/gatewayMediaApi';
import gaApi from 'app/initialization/ga/ga-api';
import { SearchFilterService } from '@legacy-components/filters/search-filter-service';
import { SEARCH_FILTERS as SearchFiltersConst } from '@search-filters/constants/searchFilters';
import { allLabelsModalActions } from '@legacy-components/content/content-library-labels-modal/redux/allLabelsModalSlice';

const content = angular.module('curalate');

content.component('contentLibraryLabelsFilterSection', {
  bindings: {
    uniqueKey: '@?',
    collectionId: '<',
  },
  require: {
    searchFilterSet: '^searchFilterSet',
  },
  template: require('@legacy-components/content/content-library-labels-filter-section.html'),
  controller: [
    '$element',
    '$scope',
    '$ngRedux',
    'AutocompleteTypes',
    'ContentLibraryApiService',
    'rx',
    'SearchFilterService',
    'SEARCH_FILTERS',
    function (
      $element,
      $scope,
      $ngRedux,
      AutocompleteTypes,
      ContentLibraryApiService,
      rx,
      SearchFilterService: SearchFilterService,
      SEARCH_FILTERS: typeof SearchFiltersConst,
    ) {
      const LABELS_COMPONENT_NAME = 'labelsFilter';
      const self = this;
      let ngUnsubscribe;
      let filters = {};
      let suggestionPromise;

      $scope.model = {
        searchLabelOptions: [],
        mostUsedOptions: [],
        labelsSearchText: '',
        asyncSearchOutstanding: false,
        allLabels: [],
        initiallySelectedLabels: [],
      };

      this.unsubscribe = $ngRedux.connect(
        () => ({
          // TODO: Migrated the entire filter section to React, The Manage Labels modal has already been migrated to React.
          isManageLabelsModalOpen: false,
        }),
        allLabelsModalActions,
      )(this);

      const labelsCallbackAsync = (query?: string) => {
        const params = {
          // Overwritten by the object unpacking, if the field exists.
          // This defaults to "All" on the backend, which is all touched media (tagged, rights requested, etc...)
          contentGroup: 'Nothing',
          ...filters,
          collectionId: self.collectionId,
        };

        if (suggestionPromise) {
          suggestionPromise.abort();
          suggestionPromise = null;
        }

        $scope.model.searchLabelOptions = null;
        suggestionPromise =
          ContentLibraryApiService.getAutocompleteSuggestionsAsync(
            AutocompleteTypes.LABELS,
            query,
            params,
          );
        suggestionPromise.finally(() => {
          suggestionPromise = null;
        });

        return suggestionPromise;
      };

      const updateLabelsFromCallback = (labelsSearchString?: string) => {
        return labelsCallbackAsync(labelsSearchString).then((result) => {
          if (result) {
            self.initialLabelsCallbackComplete = true;
            const processedLabels = _(result.labels).map((userLabel: any) => {
              return {
                value: userLabel.value,
                label: userLabel.value,
                count: userLabel.count,
              };
            });

            if (labelsSearchString) {
              $scope.model.searchLabelOptions = processedLabels;
            } else {
              $scope.model.mostUsedOptions = processedLabels;
            }
          }
        });
      };

      const lazyUpdateLabels = _.debounce(() => {
        updateLabelsFromCallback();
      }, 200);

      const updateFilters = (newFilters) => {
        const oldFilters = angular.copy(filters);

        filters = _(filters).extend(newFilters);

        if (!angular.equals(oldFilters, filters)) {
          lazyUpdateLabels();
        }
      };

      $scope.getIsManageLabelsModalOpen = () => {
        return this.isManageLabelsModalOpen;
      };

      $scope.openViewAllLabelsModal = async () => {
        const currentlySelectedLabels =
          this.searchFilterSet.getFilterState()?.labels ?? [];

        this.setViewAllModalOpen(true);
        $scope.model.initiallySelectedLabels = currentlySelectedLabels;
      };

      $scope.onViewAllLabelsModalClose = (args: {
        labels?: string[];
        shouldRefresh?: boolean;
      }) => {
        this.setViewAllModalOpen(false);

        if (args.labels) {
          this.searchFilterSet.updateFilter('labels', args.labels);
        }

        if (args.shouldRefresh) {
          updateLabelsFromCallback();
        }
      };

      $scope.labelsSearchTextChange = () => {
        updateLabelsOptionsFromSearchText();
      };

      $scope.clearButtonClicked = () => {
        $scope.model.labelsSearchText = '';
        updateLabelsOptionsFromSearchText();
        focusSearch();
      };

      const updateLabelsOptionsFromSearchText = () => {
        $scope.model.asyncSearchOutstanding = true;
        const labelsSearchString = $scope.model.labelsSearchText;

        updateLabelsFromCallback(labelsSearchString).finally(() => {
          $scope.model.asyncSearchOutstanding = false;
          gaApi.recordEvent(
            self.trackingCategory,
            gaApi.ACTION_COLLECTIONS_SEARCH_SOURCES,
            'Labels',
          );
        });
      };

      const focusSearch = () => {
        setTimeout(() => {
          const searchInput = $element.find(
            '#content-library-labels-filter-search-input',
          );
          searchInput.trigger('focus');
        }, 0);
      };

      this.$onInit = () => {
        const quickSearchObservable =
          SearchFilterService.getObservableForFilterSet(
            SEARCH_FILTERS.COLLECTION_CONTENT_FILTERS_QUICK_SEARCH,
          );
        const searchFilterObservable =
          SearchFilterService.getFilterSetObservable();

        const offset = 0;
        const limit = 100;

        ngUnsubscribe = new rx.Subject();

        searchFilterObservable
          .takeUntil(ngUnsubscribe)
          .subscribe((newFilters) => {
            // Omit labels so that they are ignored when searching for more labels (labels are always ORed together)
            const newSetObject = _(newFilters.setObject).omit('labels');
            updateFilters(newSetObject);
          });

        quickSearchObservable
          .takeUntil(ngUnsubscribe)
          .subscribe((newFilters) => {
            const searchTextCategory: any = _(
              newFilters.setCategories,
            ).findWhere({ key: 'searchText' });
            let searchText = [];

            if (searchTextCategory) {
              searchText = searchTextCategory.options;
            }

            updateFilters({ searchText });
          });

        const sourcesDropdownInitialState =
          SearchFilterService.getRegisteredSet(
            SEARCH_FILTERS.CONTENT_LIBRARY_USERS_DROPDOWN_FILTERS,
          );
        if (sourcesDropdownInitialState) {
          updateFilters(sourcesDropdownInitialState.params);
        }

        const streamSourcesInitialState = SearchFilterService.getRegisteredSet(
          SEARCH_FILTERS.STREAM_SOURCES,
        );
        if (streamSourcesInitialState) {
          updateFilters(streamSourcesInitialState.params);
        }

        // Quick query to see if the client at least 5 labels
        gatewayMediaApi.getMostCommonLabels(6).then((labels) => {
          $scope.model.clientHasAnyLabels = labels.length > 0;

          // Only show view all labels if the client has at least 5 labels, total
          $scope.model.showViewAllLabels = labels.length >= 5;
          self.searchFilterSet.completeLoadingState(LABELS_COMPONENT_NAME);
        });

        if (self.searchFilterSet.isSetLoadable) {
          self.isLoading = true;
          self.searchFilterSet.setLoadingState(LABELS_COMPONENT_NAME);
          self.searchFilterSet.loadingStateObservable.subscribe(
            (isSetLoading) => {
              self.isLoading = isSetLoading;
            },
          );
        } else {
          self.isLoading = false;
        }
      };

      this.$onDestroy = () => {
        ngUnsubscribe.next();
        ngUnsubscribe.complete();
        this.unsubscribe();
      };
    },
  ],
});

export {};
